import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Chip,
  Link,
} from "@mui/material";
import { useBillings } from "../contexts/BillingContext";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization2 } from "../contexts/OrganizationContext2";
import { useLocation, useNavigate } from "react-router-dom"; // To detect return from Stripe
import { priceIdToPlanKey } from "../pricingtable/planConfigs";

const BillingSettingsPage = () => {
  const {
    currentSubscription,
    redirectToCustomerPortal,
    syncStripeSubscriptionDetails,
  } = useBillings();
//  const { userProfile } = useAuth();
  const navigate = useNavigate();

 const { userProfile, currentUser } = useAuth();   // need UID for cache-key

  const { organization } = useOrganization2();
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const [toastOpen, setToastOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false); // New state for redirect screen

  const location = useLocation(); // Detect if user came back from Stripe

  /* ────────────────────────────────────────────────────────────────
   * Stripe return-processing guards
   *
   * Why:
   * - Returning from Stripe can re-run effects during hydration and in
   *   React StrictMode development flows.
   * - We only want to process a given Stripe-return URL once per mount.
   * - We also want to wait until the Stripe customer identity is actually
   *   available before attempting a sync.
   * ───────────────────────────────────────────────────────────── */
  const stripeReturnHandledRef = useRef(false);
  const stripeReturnKeyRef = useRef("");

/**
 * Detect if returning from Stripe portal / Stripe-hosted billing flows
 * and trigger exactly one sync after billing identity is ready.
 */
useEffect(() => {
  const params = new URLSearchParams(location.search);

  const cameFromStripePortal =
    params.get("fromStripePortal") === "true";

  const cameFromStripeCheckout =
    params.get("status") === "success";

  // Trigger a resync when returning from either:
  //   • Stripe Customer Portal  → ?fromStripePortal=true
  //   • Stripe Checkout success → ?status=success
  const cameFromStripe =
    cameFromStripePortal || cameFromStripeCheckout;

  const resolvedStripeCustomerId =
    currentSubscription?.customerId ||
    organization?.stripeCustomerId ||
    null;

  const returnKey =
    `${location.pathname}${location.search}`;

  if (!cameFromStripe) {
    stripeReturnHandledRef.current = false;
    stripeReturnKeyRef.current = "";
    return;
  }

  if (!resolvedStripeCustomerId) {
    console.log(
      "[BillingSettingsPage] Stripe return detected, but billing " +
      "identity is not ready yet. Waiting before syncing.",
      {
        returnKey,
        currentSubscriptionCustomerId:
          currentSubscription?.customerId || null,
        organizationStripeCustomerId:
          organization?.stripeCustomerId || null,
      }
    );
    return;
  }

  if (
    stripeReturnHandledRef.current &&
    stripeReturnKeyRef.current === returnKey
  ) {
    console.log(
      "[BillingSettingsPage] Stripe return already processed for " +
      "this location. Skipping duplicate sync.",
      {
        returnKey,
        customerId: resolvedStripeCustomerId,
      }
    );
    return;
  }

  stripeReturnHandledRef.current = true;
  stripeReturnKeyRef.current = returnKey;

  console.log(
    "[BillingSettingsPage] Detected return from Stripe. " +
    "Syncing and clearing cache…",
    {
      returnKey,
      customerId: resolvedStripeCustomerId,
    }
  );

  (async () => {
    try {
      await syncStripeSubscriptionDetails({
        stripeCustomerIdOverride: resolvedStripeCustomerId,
      });

      // 🔹 invalidate only this user’s cached standing
      if (currentUser?.uid) {
        sessionStorage.removeItem(
          `accountInGoodStanding_${currentUser.uid}`
        );
        console.log(
          `[BillingSettingsPage] Cleared standing cache for ` +
          `${currentUser.uid}`
        );
      }
    } catch (err) {
      console.error(
        "[BillingSettingsPage] Post-portal sync failed:",
        err.message
      );
    }
  })();
}, [
  location.pathname,
  location.search,
  syncStripeSubscriptionDetails,
  currentUser?.uid,
  currentSubscription?.customerId,
  organization?.stripeCustomerId,
]);

/* ────────────────────────────────────────────────────────────────
 * hardening:
 * If a scheduled change exists, verify it still exists server-side.
 * Your syncStripeSubscriptionDetails() already:
 *   • checks scheduleId in Stripe
 *   • clears pendingStripeSchedule if canceled/released/completed/missing
 * So we just trigger one sync on page load when scheduleId exists.
 * ──────────────────────────────────────────────────────────────── */
useEffect(() => {
  const scheduleId =
    currentSubscription?.pendingStripeSchedule?.scheduleId || null;

  if (!scheduleId) return;

  console.log(
    "[BillingSettingsPage] pending schedule detected; verifying…",
    scheduleId
  );

  (async () => {
    try {
      await syncStripeSubscriptionDetails();
    } catch (err) {
      console.error(
        "[BillingSettingsPage] schedule verify sync failed:",
        err.message
      );
    }
  })();
}, [
  currentSubscription?.pendingStripeSchedule?.scheduleId,
  syncStripeSubscriptionDetails,
]);

  /**
   * Handles redirecting the user to the Stripe Customer Portal.
   */
const handleOpenPortal = async () => {
  setIsLoading(true);
  try {
    console.log("[BillingSettingsPage] Redirecting user to Stripe...");
    
    // Ensure customerId is retrieved correctly
    const customerId = currentSubscription?.customerId || organization?.stripeCustomerId;
    console.log("[BillingSettingsPage] Using customerId:", customerId);

    if (!customerId) {
      throw new Error("Missing customerId for Stripe portal session.");
    }

    /* --- call the callable Cloud Function instead --- */
    const { httpsCallable } = await import("firebase/functions");
    const { functions }     = await import("../firebase");

    const openPortal = httpsCallable(functions, "createCustomerPortalSession");
    const { data }   = await openPortal({
      customerId,
      returnUrl:
        window.location.origin +
        "/settings/billing-settings?fromStripePortal=true",
    });

    if (!data?.url) {
      throw new Error("No URL returned from customer portal session");
    }

    console.log("[BillingSettingsPage] Redirecting to customer portal URL:", data.url);
    setIsRedirecting(true);
    setTimeout(() => {
      window.location.href = data.url;
    }, 2000);

  } catch (error) {
    console.error("[BillingSettingsPage] Error opening customer portal:", error);
    setToastMessage("Error opening customer portal. Please try again.");
    setToastSeverity("error");
    setToastOpen(true);
  } finally {
    setIsLoading(false);
  }
};

const handleChangePlan = () => {
  // Optional: pass current seats / plan so SubscriptionPage can pre-select
  navigate("/subscription", {
    state: {
      from: "billing-settings",
      preselect: {
        planName   : currentSubscription.planName,
        seatCount  : currentSubscription.seats,
      },
    },
  });
};


  const handleCloseToast = () => setToastOpen(false);

  // Show redirect screen if redirecting to Stripe portal
  if (isRedirecting) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          textAlign: "center",
        }}
      >
        <CircularProgress sx={{ mb: 3 }} />
        <Typography variant="h6">
          You are being redirected to your billing portal...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Please wait while we securely transfer you to your billing portal. Do
          not close this window.
        </Typography>
      </Box>
    );
  }

  // Show a loading spinner while waiting for subscription details to load
  if (!currentSubscription) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  /*───────────────────────────────────────────────────────────────
   *  📌  Billing-state helpers  (add *once*, right here)
   *──────────────────────────────────────────────────────────────*/
  const PAST_DUE_STATES = [
    "past_due",
    "unpaid",
    "incomplete",
    "incomplete_expired",
  ];

  // Is the subscription in one of the “needs-payment” states?
  const isPastDue   = PAST_DUE_STATES.includes(currentSubscription.status);

  // Colour helper for the status <Chip> (used later)
  const statusColor =
    isPastDue
      ? "error"                                // red
      : currentSubscription.status === "trialing"
      ? "info"                                 // blue
      : "success";                             // green (active)

  // Scheduled downgrade / seat reduction (Option B)
  const scheduled = currentSubscription.pendingStripeSchedule || null;
  const hasScheduledChange = Boolean(scheduled?.effectiveAt);
  const scheduledDate = hasScheduledChange ?
    new Date(scheduled.effectiveAt).toLocaleDateString(
      undefined,
      { month: "short", day: "numeric", year: "numeric" },
    ) :
    null;

  // scheduled change details (best-effort display)
  const scheduledSeats =
    Number.isFinite(+scheduled?.newSeatCount) ?
      Number(scheduled.newSeatCount) :
      null;

  const seatsWillChange =
    scheduledSeats != null &&
    Number.isFinite(+currentSubscription?.seats) &&
    scheduledSeats !== Number(currentSubscription.seats);

  // Best-effort plan label from priceId → plan key
  const scheduledPlanKey =
    scheduled?.newPriceId && priceIdToPlanKey[scheduled.newPriceId] ?
      priceIdToPlanKey[scheduled.newPriceId] :
      null;

  const scheduledPlanName =
    scheduledPlanKey ?
      scheduledPlanKey.charAt(0).toUpperCase() + scheduledPlanKey.slice(1) :
      null;

  const planWillChange =
    Boolean(scheduledPlanName) &&
    scheduledPlanName !== currentSubscription?.planName;

  // Render subscription details with Manage Billing button
  return (
  <Paper sx={{ p: 3, width: "100%", minHeight: "500px" }}>
    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Billing Settings
      </Typography>

      {/*────────────────  Past-due banner  ────────────────*/}
      {isPastDue && (
      <Alert
        severity="warning"
        variant="filled"          // <- filled makes it more obvious
        icon={false}              // <- cleaner look (optional)
        sx={{ mb: 3, fontWeight: 500 }}
      >
          Your subscription is <strong>past due</strong>. <br />
          Please add a valid payment method or update the existing one below and
          we’ll retry the charge automatically to restore full access.
        </Alert>
      )}

      {/*────────────────  Scheduled change banner  ───────────────*/}
      {hasScheduledChange && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 3 }}
        >
          Your changes will take effect on{" "}
          <strong>{scheduledDate}</strong>.{" "}
          {planWillChange && (
            <>
              Your plan will change from{" "}
              <strong>{currentSubscription.planName}</strong> to{" "}
              <strong>{scheduledPlanName}</strong>.{" "}
            </>
          )}
          {seatsWillChange && (
            <>
              Seats will change from{" "}
              <strong>{currentSubscription.seats}</strong> to{" "}
              <strong>{scheduledSeats}</strong>.{" "}
            </>
          )}
          You&rsquo;ll keep your current plan and seats until then.
        </Alert>
      )}

<Grid container spacing={3}>

  {/* Subscription Details */}
  <Grid item xs={12} md={6}>
    <Box>
      <Typography
        variant="subtitle1"      // ↤ match “Personal Info” style
        fontWeight="bold"
        sx={{ mb: 2 }}
      >
        Subscription Details
      </Typography>

      {/* ── Indented detail block ── */}
      <Box sx={{ pl: 2 }}>
        {/* Plan row with inline “Change plan” link */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          <Typography variant="body2">
            <strong>Plan:</strong> {currentSubscription.planName}
          </Typography>

<Link
  component="button"
  onClick={handleChangePlan}
  variant="body2"
  underline="none"          // ← remove underline even on hover
  sx={{
    textTransform: "none",
    mr: 8,                  // keep your existing right-margin
    "&:hover": {
      fontWeight: "bold",    // ← bold on hover
      textDecoration: "none" // ← ensure no underline
    },
  }}
>
  Change plan
</Link>
        </Box>


        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, ml: 1 }}
        >
          Your plan auto-renews on{" "}
          {currentSubscription.renewalDate
            ? new Date(currentSubscription.renewalDate).toLocaleDateString(
                undefined,
                { month: "short", day: "numeric", year: "numeric" }
              )
            : "—"}
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Seats:</strong> {currentSubscription.seats}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>$ / Seat:</strong> ${parseFloat(currentSubscription.price).toFixed(2)}
          &nbsp;per&nbsp;{currentSubscription.interval}
        </Typography>


        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body2">
            <strong>Status:</strong>
          </Typography>
          <Chip
            label={currentSubscription.status}
            color={statusColor}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        </Box>

      </Box>
    </Box>
  </Grid>

        {/* Action Section */}
        <Grid item xs={12} md={6}>
          <Box>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: 1 }}
          >
            Manage Payment
          </Typography>

            <Button
              onClick={handleOpenPortal}
              variant={isPastDue ? "contained" : "outlined"}
              color={isPastDue ? "error" : "primary"}
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? <CircularProgress size={24} /> : "Manage Billing"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BillingSettingsPage;
